import { useCallback, useEffect, useState } from "react";
import { anlookAxiosInstance } from "../api/axiosInstance";
import { ENDPOINTS } from "../api/endpoints";

const initialPricing = {
  subTotal: 0,
  cgst: 0,
  sgst: 0,
  total: 0,
  paymentMode: "",
  deliveryFee: 0,
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const pickFirstValue = (...values) => {
  return values.find(
    (value) =>
      value !== undefined && value !== null && String(value).trim() !== "",
  );
};

export const useOrderDetails = (taskId) => {
  const [orderDetails, setOrderDetails] = useState(null);
  const [products, setProducts] = useState([]);
  const [pricing, setPricing] = useState(initialPricing);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchOrderDetails = useCallback(async () => {
    if (!taskId) {
      setOrderDetails(null);
      setProducts([]);
      setPricing(initialPricing);
      setError("");
      return null;
    }

    try {
      setLoading(true);
      setError("");

      const payload = {
        orderid: String(taskId),
      };

      const res = await anlookAxiosInstance.post(
        ENDPOINTS.ORDER_DETAILS,
        payload,
      );

      if (res?.data?.responseCode !== "00") {
        throw new Error(
          res?.data?.responseMessage || "Order details fetch nahi ho payi.",
        );
      }

      const order = res?.data?.response || res?.data?.data || null;

      if (!order) {
        setOrderDetails(null);
        setProducts([]);
        setPricing(initialPricing);
        return null;
      }

      setOrderDetails(order);

      setPricing({
        subTotal: toNumber(
          pickFirstValue(
            order?.subtotal,
            order?.subTotal,
            order?.sub_total,
            order?.item_total,
            order?.itemTotal,
          ),
        ),
        cgst: toNumber(pickFirstValue(order?.tax_cgst, order?.cgst)),
        sgst: toNumber(pickFirstValue(order?.tax_sgst, order?.sgst)),
        total: toNumber(
          pickFirstValue(
            order?.totalOrderPrice,
            order?.total_order_price,
            order?.pay_amount,
            order?.payAmount,
            order?.total,
            order?.grand_total,
          ),
        ),
        paymentMode: pickFirstValue(
          order?.payment_mode,
          order?.paymentMode,
          "Online",
        ),
        deliveryFee: toNumber(
          pickFirstValue(
            order?.delivery_fee,
            order?.deliveryFee,
            order?.delivery_charge,
            order?.deliveryCharge,
          ),
        ),
      });

      const orderItems =
        order?.items ||
        order?.orderItems ||
        order?.order_items ||
        order?.products ||
        order?.order_details?.items ||
        [];

      const mappedProducts = Array.isArray(orderItems)
        ? orderItems.map((item, index) => {
            const quantity = toNumber(
              pickFirstValue(item?.quantity, item?.qty, item?.item_quantity),
            );

            const unitPrice = toNumber(
              pickFirstValue(
                item?.item_price,
                item?.itemPrice,
                item?.price,
                item?.unitPrice,
              ),
            );

            return {
              id:
                pickFirstValue(item?.item_id, item?.itemId, item?.id) ||
                `${taskId}-${index}`,

              name:
                pickFirstValue(
                  item?.item_name,
                  item?.itemName,
                  item?.actualName,
                  item?.product_name,
                  item?.productName,
                  item?.product_id,
                ) || "Item",

              qty: quantity,
              unitPrice,

              itemTotal: toNumber(
                pickFirstValue(
                  item?.total_price,
                  item?.totalPrice,
                  item?.item_total,
                  item?.itemTotal,
                  item?.total,
                  unitPrice * quantity,
                ),
              ),

              image: pickFirstValue(
                item?.itemImage,
                item?.item_image,
                item?.image,
                item?.product_image,
                null,
              ),

              customization: item?.customization || "{}",
              cakeCustomization: item?.cakeCustomization || null,
            };
          })
        : [];

      setProducts(mappedProducts);

      return order;
    } catch (err) {
      console.error("Order details API error:", err);

      setError(
        err?.response?.data?.responseMessage ||
          err?.response?.data?.message ||
          err?.message ||
          "Order details fetch nahi ho payi.",
      );

      setOrderDetails(null);
      setProducts([]);
      setPricing(initialPricing);

      return null;
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  return {
    orderDetails,
    products,
    pricing,
    loading,
    error,
    fetchOrderDetails,
    fetchOrderItems: fetchOrderDetails,
  };
};
